// frontend/src/components/cash-close/MultiYearGridCell.js
import React from 'react';
import { useTheme, sans, mono, red, green } from '../../tokens';

/**
 * One cell of the multi-year variance grid. Pure render — no state.
 *
 * Props:
 *   actual         number | null   — CAD signed-negative cash actual for this dept-month
 *   forecast       number | null   — CAD signed-negative forecast for this dept-month
 *   isCurrentMonth boolean         — show highlight background
 *   isFuture       boolean         — gray + no variance
 *   isLoading      boolean         — past month, actual still in flight
 */
export default function MultiYearGridCell({ actual, forecast, isCurrentMonth, isFuture, isLoading }) {
  const t = useTheme();

  // Future month: forecast-only, muted
  if (isFuture) {
    return (
      <div style={{ ...cellBase(t), color: t.textMuted, fontFamily: mono, fontSize: 10 }}>
        {forecast == null ? '' : fmt(forecast)}
      </div>
    );
  }

  // Current month: forecast highlighted, no variance yet (proration not computed here)
  if (isCurrentMonth) {
    return (
      <div style={{ ...cellBase(t), background: '#FFF9E6', color: t.textPrimary, fontFamily: mono, fontSize: 10, fontWeight: 600 }}>
        {forecast == null ? '' : fmt(forecast)}
      </div>
    );
  }

  // Past month, still loading actuals
  if (isLoading || actual == null) {
    return (
      <div style={{ ...cellBase(t), color: t.textMuted, fontFamily: mono, fontSize: 10, opacity: 0.5 }}>
        —
      </div>
    );
  }

  // Past month with actuals: variance %
  if (forecast == null || forecast === 0) {
    return <div style={{ ...cellBase(t), color: t.textMuted }}>—</div>;
  }
  const varPct = ((Math.abs(actual) - Math.abs(forecast)) / Math.abs(forecast)) * 100;
  const color = varPct > 10 ? red : varPct < -10 ? green : t.textSecondary;
  return (
    <div style={{ ...cellBase(t), color, fontFamily: mono, fontSize: 10, fontWeight: 600 }}>
      {`${varPct > 0 ? '+' : ''}${varPct.toFixed(0)}%`}
    </div>
  );
}

const cellBase = (t) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: '0 6px',
  borderBottom: `1px solid ${t.borderLight}`,
  borderRight: `1px solid ${t.borderLight}`,
  width: '100%',
  height: '100%',
  boxSizing: 'border-box',
});

const fmt = (n) => {
  if (n == null) return '';
  const sign = n < 0 ? '-' : '';
  const v = Math.abs(n);
  if (v >= 1000) return `${sign}$${(v / 1000).toFixed(0)}k`;
  return `${sign}$${v.toFixed(0)}`;
};
